import { jsPDF } from 'jspdf';
import { TEYCO } from '../config/branding';
import { formatEUR, type QuoteResult } from '../config/quote';
import { CAPTURE_BG_HEX, type CapturedViews } from './views';

export interface SheetOptions {
  views: CapturedViews;
  /** Filas de configuración [etiqueta, valor]. */
  configRows: Array<[string, string]>;
  quote: QuoteResult;
  quoteId: string;
  date: Date;
}

const W = 1754;
const H = 1240; // A4 apaisado ≈ 150 dpi

/**
 * Compone la ficha técnica (canvas 2D): cabecera con logo Teyco, las 4 vistas
 * acotadas, cajetín de configuración, desglose de presupuesto y pie con datos de
 * contacto. Devuelve el canvas listo para descargar como PNG o PDF.
 */
export async function composeSheet(opts: SheetOptions): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // Fondo.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  drawHeader(ctx, opts);
  await drawViews(ctx, opts.views);
  drawInfoPanel(ctx, opts);
  drawFooter(ctx, opts);

  return canvas;
}

// ---------------- secciones ----------------
function drawHeader(ctx: CanvasRenderingContext2D, opts: SheetOptions): void {
  // Logo Teyco (placeholder: rótulo azul; sustituible por el oficial).
  roundRect(ctx, 40, 26, 156, 56, 8);
  ctx.fillStyle = TEYCO.primary;
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.font = '800 30px Segoe UI, Arial, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText('TEYCO', 118, 56);

  // Título.
  ctx.textAlign = 'left';
  ctx.fillStyle = TEYCO.dark;
  ctx.font = '700 30px Segoe UI, Arial, sans-serif';
  ctx.fillText('Ficha técnica · Pérgola Bioclimática', 218, 46);
  ctx.fillStyle = '#5a6270';
  ctx.font = '400 17px Segoe UI, Arial, sans-serif';
  ctx.fillText(`${TEYCO.line} · ${TEYCO.name}`, 218, 74);

  // Identificador y fecha (derecha).
  ctx.textAlign = 'right';
  ctx.fillStyle = TEYCO.dark;
  ctx.font = '700 18px Segoe UI, Arial, sans-serif';
  ctx.fillText(opts.quoteId, W - 40, 46);
  ctx.fillStyle = '#5a6270';
  ctx.font = '400 15px Segoe UI, Arial, sans-serif';
  ctx.fillText(opts.date.toLocaleString('es-ES'), W - 40, 72);

  ctx.strokeStyle = '#d6dadf';
  ctx.lineWidth = 1;
  line(ctx, 40, 104, W - 40, 104);
}

async function drawViews(ctx: CanvasRenderingContext2D, views: CapturedViews): Promise<void> {
  const cells: Array<{ key: keyof CapturedViews; caption: string; col: number; row: number }> = [
    { key: 'front', caption: 'Alzado frontal', col: 0, row: 0 },
    { key: 'side', caption: 'Alzado lateral', col: 1, row: 0 },
    { key: 'plan', caption: 'Planta (cenital)', col: 0, row: 1 },
    { key: 'iso', caption: 'Vista isométrica', col: 1, row: 1 },
  ];

  const areaX = 40;
  const areaY = 124;
  const gap = 22;
  const cellW = (1040 - gap) / 2;
  const cellH = (986 - gap) / 2;
  const capH = 34;

  for (const c of cells) {
    const x = areaX + c.col * (cellW + gap);
    const y = areaY + c.row * (cellH + gap);

    roundRect(ctx, x, y, cellW, cellH, 10);
    ctx.fillStyle = CAPTURE_BG_HEX;
    ctx.fill();
    ctx.strokeStyle = '#d6dadf';
    ctx.lineWidth = 1;
    ctx.stroke();

    const img = await loadImage(views[c.key]);
    drawContained(ctx, img, x + 10, y + 10, cellW - 20, cellH - capH - 16);

    ctx.fillStyle = '#ffffff';
    roundRectPath(ctx, x + 12, y + cellH - capH + 2, cellW - 24, capH - 8, 6);
    ctx.fill();
    ctx.fillStyle = TEYCO.dark;
    ctx.font = '600 16px Segoe UI, Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(c.caption, x + 22, y + cellH - capH / 2);
  }
}

function drawInfoPanel(ctx: CanvasRenderingContext2D, opts: SheetOptions): void {
  const x = 1110;
  const w = W - 40 - x;
  let y = 140;

  // --- Configuración ---
  sectionTitle(ctx, 'CONFIGURACIÓN', x, y);
  y += 30;
  ctx.font = '400 16px Segoe UI, Arial, sans-serif';
  for (const [k, v] of opts.configRows) {
    ctx.fillStyle = '#5a6270';
    ctx.textAlign = 'left';
    ctx.fillText(k, x, y);
    ctx.fillStyle = TEYCO.dark;
    ctx.textAlign = 'right';
    ctx.fillText(v, x + w, y);
    y += 27;
  }

  // --- Presupuesto ---
  y += 14;
  sectionTitle(ctx, 'PRESUPUESTO', x, y);
  y += 28;
  ctx.font = '400 15px Segoe UI, Arial, sans-serif';
  for (const l of opts.quote.lines) {
    ctx.fillStyle = '#5a6270';
    ctx.textAlign = 'left';
    ctx.fillText(clip(l.label, 38), x, y);
    ctx.fillStyle = TEYCO.dark;
    ctx.textAlign = 'right';
    ctx.fillText(formatEUR(l.amount), x + w, y);
    y += 24;
  }
  y += 4;
  ctx.strokeStyle = '#d6dadf';
  line(ctx, x, y, x + w, y);
  y += 22;

  row(ctx, x, w, y, 'Subtotal', formatEUR(opts.quote.subtotal), false);
  y += 26;
  if (opts.quote.minApplied) {
    row(ctx, x, w, y, 'Precio mínimo aplicado', formatEUR(opts.quote.base), false);
    y += 26;
  }
  row(ctx, x, w, y, `IVA (${Math.round(opts.quote.ivaRate * 100)}%)`, formatEUR(opts.quote.iva), false);
  y += 30;

  // Total destacado.
  roundRect(ctx, x, y, w, 56, 10);
  ctx.fillStyle = TEYCO.primary;
  ctx.fill();
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = '700 17px Segoe UI, Arial, sans-serif';
  ctx.fillText('TOTAL (PVP)', x + 16, y + 28);
  ctx.textAlign = 'right';
  ctx.font = '800 26px Segoe UI, Arial, sans-serif';
  ctx.fillText(formatEUR(opts.quote.total), x + w - 16, y + 29);
}

function drawFooter(ctx: CanvasRenderingContext2D, _opts: SheetOptions): void {
  const y = 1150;
  ctx.strokeStyle = '#d6dadf';
  line(ctx, 40, y, W - 40, y);

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = TEYCO.dark;
  ctx.font = '700 15px Segoe UI, Arial, sans-serif';
  ctx.fillText(TEYCO.name, 40, y + 26);
  ctx.fillStyle = '#5a6270';
  ctx.font = '400 14px Segoe UI, Arial, sans-serif';
  ctx.fillText(TEYCO.address, 40, y + 48);
  ctx.fillText(`Tel: ${TEYCO.phone} · ${TEYCO.email} · ${TEYCO.web}`, 40, y + 70);

  ctx.textAlign = 'right';
  ctx.fillStyle = TEYCO.dark;
  ctx.font = '700 16px Segoe UI, Arial, sans-serif';
  ctx.fillText(TEYCO.brands.join('  ·  '), W - 40, y + 30);
  ctx.fillStyle = '#9aa3ad';
  ctx.font = '400 13px Segoe UI, Arial, sans-serif';
  ctx.fillText('Documento generado automáticamente · demo', W - 40, y + 56);
}

// ---------------- descargas ----------------
export function downloadSheetPNG(canvas: HTMLCanvasElement, filename: string): void {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    triggerDownload(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }, 'image/png');
}

export function downloadSheetPDF(canvas: HTMLCanvasElement, filename: string): void {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const img = canvas.toDataURL('image/jpeg', 0.92);
  pdf.addImage(img, 'JPEG', 0, 0, 297, 210);
  pdf.save(filename);
}

// ---------------- helpers ----------------
function sectionTitle(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  ctx.fillStyle = TEYCO.primary;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = '700 15px Segoe UI, Arial, sans-serif';
  ctx.fillText(text, x, y);
  ctx.textBaseline = 'middle';
}

function row(ctx: CanvasRenderingContext2D, x: number, w: number, y: number, k: string, v: string, bold: boolean): void {
  ctx.font = `${bold ? '700' : '600'} 16px Segoe UI, Arial, sans-serif`;
  ctx.fillStyle = '#5a6270';
  ctx.textAlign = 'left';
  ctx.fillText(k, x, y);
  ctx.fillStyle = TEYCO.dark;
  ctx.textAlign = 'right';
  ctx.fillText(v, x + w, y);
}

function drawContained(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  bx: number,
  by: number,
  bw: number,
  bh: number,
): void {
  const scale = Math.min(bw / img.width, bh / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, bx + (bw - dw) / 2, by + (bh - dh) / 2, dw, dh);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  roundRectPath(ctx, x, y, w, h, r);
}
function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}
function clip(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
